<template>
  <div class="project-wrapper">
    <div class="project-container">
      <div class="project-content">
        <p class="project-current-step">{{ Translator('soprasolar.project.step1.step') }}</p>
        <h1 class="project-title">{{ Translator('soprasolar.project.step1.title') }}</h1>
        <p class="project-description">{{ Translator('soprasolar.project.step1.description') }}</p>
      </div>
      <div class="project-content">
        <form @submit="onSubmit" ref="form" id="formInformations" class="std-form">
          <div class="project-warning" v-if="isErrorCompany">
            <i class="ic ic-info" aria-hidden="true"></i>
            <p class="project-warning-message">{{ Translator('soprasolar.project.step1.warning_company') }}</p>
          </div>
          <template v-if="!form.selectedCompany && !form.isNewCompany">
            <div class="form-group">
              <div class="form-label-container">
                <label for="department" class="control-label required">
                  {{ Translator('soprasolar.project.step1.form.department') }}
                </label>
              </div>
              <select v-model="form.department" id="department" required>
                <option :value="department.code" v-for="department in departments" :key="department.id">{{ department.name }}</option>
              </select>
            </div>
            <p style="text-align: right;">
              <button type="button" v-if="!form.department" class="form-retry" @click="onNewCompany()" style="text-decoration: underline;">{{ Translator('soprasolar.project.step1.form.company_international') }}</button>
            </p>
            <template v-if="form.department">
              <div class="form-group">
                <div class="form-label-container">
                  <label for="search" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.company') }}
                  </label>
                </div>
                <div class="project-fake-input" :class="{'is-loading': isSearching}">
                  <i class="ic ic-search" aria-hidden="true"></i>
                  <input type="text" id="search" class="form-control" required v-model="search" @keyup="onKeyup($event)">
                </div>
              </div>
              <p class="project-hint" v-if="establishments === null && companies === null">{{ Translator('soprasolar.project.step1.form.company_hint') }}</p>
              <ul v-if="establishments !== null || companies !== null" class="project-propositions">
                <li v-for="establishment in establishments" :key="establishment.id" class="project-propositions-item">
                  <button type="button" @click="onSelectEstablishment(establishment)" class="project-propositions-button" :class="{'is-loading': isLoadingEstablishment === establishment}">
                    <strong><i class="ic ic-establishment" aria-hidden="true"></i> {{ establishment.raisonsociale }}</strong><br>
                    <span>SIREN : {{ format(establishment.siren) }}</span><br>
                    <span>Adresse : <template v-if="establishment.adresse">{{ establishment.adresse }} - </template> {{ establishment.cpville }}</span>
                  </button>
                </li>
                <li class="project-propositions-item project-propositions-item--empty" v-if="(!establishments || establishments.length == 0) && (!companies || companies.length == 0)">
                  Aucun résultat
                </li>
                <li class="project-propositions-item project-propositions-item--add">
                  <button type="button" @click="onNewCompany()" class="project-propositions-add">
                    <i class="ic ic-add" aria-hidden="true"></i>
                    Ajouter une nouvelle entreprise
                  </button>
                </li>
              </ul>
            </template>
          </template>
          <template v-if="form.selectedCompany || form.isNewCompany">
            <p style="text-align: right;">
              <button type="button" @click="onClear()" class="form-retry" v-if="form.selectedCompany">
                <i class="ic ic-search-retry" aria-hidden="true"></i>
                Choisir une autre entreprise
              </button>
            </p>
            <div class="form-group">
              <div class="form-label-container">
                <label for="company" class="control-label required">
                  {{ Translator('soprasolar.project.step1.form.company') }}
                </label>
              </div>
              <input :readonly="form.selectedCompany" type="text" id="company" class="form-control" v-model="form.company" required>
            </div>
            <div class="form-row">
              <div class="form-group">
                <div class="form-label-container">
                  <label for="siret" class="control-label" :class="{'required': form.selectedCompany}">
                    {{ Translator('soprasolar.project.step1.form.siret') }}
                  </label>
                </div>
                <input :readonly="form.selectedCompany" type="text" id="siret" class="form-control" v-model="form.siret" :required="form.selectedCompany">
              </div>
              <div class="form-group">
                <div class="form-label-container">
                  <label for="siren" class="control-label" :class="{'required': form.selectedCompany}">
                    {{ Translator('soprasolar.project.step1.form.siren') }}
                  </label>
                </div>
                <input :readonly="form.selectedCompany" type="text" id="siren" class="form-control" v-model="form.siren" :required="form.selectedCompany">
              </div>
            </div>
            <div class="form-group">
              <div class="form-label-container">
                <label for="street" class="control-label" :class="{'required': form.selectedCompany}">
                  {{ Translator('soprasolar.project.step1.form.street') }}
                </label>
              </div>
              <input type="text" id="street" class="form-control" v-model="form.street" :required="form.selectedCompany">
            </div>
            <div class="form-row">
              <div class="form-group">
                <div class="form-label-container">
                  <label for="zipCode" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.zip_code') }}
                  </label>
                </div>
                <input type="text" id="zipCode" class="form-control" v-model="form.zipCode" required>
              </div>
              <div class="form-group">
                <div class="form-label-container">
                  <label for="city" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.city') }}
                  </label>
                </div>
                <input type="text" id="city" class="form-control" v-model="form.city" required>
              </div>
              <div class="form-group">
                <div class="form-label-container">
                  <label for="city" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.country') }}
                  </label>
                </div>
                <select class="form-control" v-model="form.country" id="country" required>
                  <option v-for="country in countries" :key="country.code" :value="country.code">{{ country.name }}</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label-container">
                <label for="occupation" class="control-label required">
                  {{ Translator('soprasolar.project.step1.form.occupation') }}
                </label>
              </div>
              <select class="form-control" v-model="form.occupation" id="occupation" required>
                <option value="roofer">{{ Translator('soprasolar.project.step1.form.occupation_options.roofer') }}</option>
                <option value="solar_installer">{{ Translator('soprasolar.project.step1.form.occupation_options.solar_installer') }}</option>
                <option value="carpenter">{{ Translator('soprasolar.project.step1.form.occupation_options.carpenter') }}</option>
                <option value="architect">{{ Translator('soprasolar.project.step1.form.occupation_options.architect') }}</option>
                <option value="drawing_office_thermal">{{ Translator('soprasolar.project.step1.form.occupation_options.drawing_office_thermal') }}</option>
                <option value="drawing_office_all_trades">{{ Translator('soprasolar.project.step1.form.occupation_options.drawing_office_all_trades') }}</option>
                <option value="drawing_office_fluid">{{ Translator('soprasolar.project.step1.form.occupation_options.drawing_office_fluid') }}</option>
                <option value="drawing_office_structural">{{ Translator('soprasolar.project.step1.form.occupation_options.drawing_office_structural') }}</option>
                <option value="drawing_office_whood">{{ Translator('soprasolar.project.step1.form.occupation_options.drawing_office_whood') }}</option>
                <option value="prime_contractor_private_businesses">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_businesses') }}</option>
                <option value="prime_contractor_private_residential">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_residential') }}</option>
                <option value="prime_contractor_private_warehouses">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_warehouses') }}</option>
                <option value="prime_contractor_private_offices">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_offices') }}</option>
                <option value="prime_contractor_private_industrial">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_industrial') }}</option>
                <option value="prime_contractor_private_data_center">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_data_center') }}</option>
                <option value="prime_contractor_private_data_loisirs">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_data_loisirs') }}</option>
                <option value="prime_contractor_private_data_others">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_private_data_others') }}</option>
                <option value="prime_contractor_public_institutions">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_public_institutions') }}</option>
                <option value="prime_contractor_public_landlords">{{ Translator('soprasolar.project.step1.form.occupation_options.prime_contractor_public_landlords') }}</option>
                <option value="other">{{ Translator('soprasolar.project.step1.form.occupation_options.other') }}</option>
              </select>
            </div>
            <fieldset>
              <legend>Contact de la demande</legend>
              <div class="form-group">
                <div class="form-label-container">
                  <label for="firstName" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.first_name') }}
                  </label>
                </div>
                <input type="text" id="firstName" class="form-control" v-model="form.firstName" required>
              </div>
              <div class="form-group">
                <div class="form-label-container">
                  <label for="lastName" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.last_name') }}
                  </label>
                </div>
                <input type="text" id="lastName" class="form-control" v-model="form.lastName" required>
              </div>
              <div class="form-group">
                <div class="form-label-container">
                  <label for="email" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.email') }}
                  </label>
                </div>
                <input type="email" id="email" class="form-control" v-model="form.email" required>
              </div>
              <div class="form-group">
                <div class="form-label-container">
                  <label for="phoneNumber" class="control-label required">
                    {{ Translator('soprasolar.project.step1.form.phone_number') }}
                  </label>
                </div>
                <input type="tel" id="phoneNumber" class="form-control" v-model="form.phoneNumber" required>
              </div>
            </fieldset>
          </template>
        </form>
      </div>
    </div>
    <div class="project-footer">
      <div class="project-actions">
        <button type="submit" class="button-flat" form="formInformations" :disabled="isLoading">
          {{ Translator('soprasolar.project.next') }}
          <i class="ic ic-arrow-right" v-if="!isLoading"></i>
          <span class="button-loader" v-if="isLoading"></span>
        </button>
        <button type="button" class="button-link" @click="onPrevious">
          <i class="ic ic-arrow-left"></i>
          {{ Translator('soprasolar.project.previous') }}
        </button>
      </div>
    </div>
    <p class="project-legend">* {{ Translator('soprasolar.project.required_field') }}</p>
  </div>
</template>
<script>
import debounce from 'debounce';
import countryList from 'country-list-js';
import FormControl from 'aw-front-helpers/classes/formControl';
import AppControlService from '@/soprasolar-project/services/AppControl';
import AppControl from '@/soprasolar-project/services/AppControl';
import SocieteApiService from '@/soprasolar-project/services/SocieteApiService';
import gsap from 'gsap/all';

export default {
  name: 'step-1',
  components: {},
  data () {
    return {
      datas: AppControlService.datas,
      countries: [],
      companies: null,
      departments: window.config.departments,
      establishments: null,
      isLoadingCompany: false,
      isLoadingEstablishment: false,
      isSearching: false,
      search: null,
      isLoading: false,
      isErrorCompany: false,
      form: {
        isNewCompany: false,
        selectedCompany: false,
        department: null,
        country: 'FR'
      }
    }
  },

  created() {
    this.getDatas = debounce( this.getDatas, 2000 );
    AppControlService.on('datas', this._onUpdateDatas);
    const countries = [];
    const forbiddenCountries = ["Guadeloupe", "Martinique", "French Guiana", "Reunion", "Mayotte"];

    for (let code in countryList.all) {
      const countryName = countryList.all[code].name;
      const countryCode = countryList.all[code].iso2;
      if (!forbiddenCountries.includes(countryName)) {
        countries.push({
          code:countryCode,
          name: countryName
        });
      }
    }

    countries.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
    this.countries = countries;
  },

  beforeDestroy() {
    AppControlService.off('datas', this._onUpdateDatas);
  },

  mounted() {
    this._form = new FormControl(this.$refs.form);
    if(this.datas.step1) this.form = this.datas.step1;
  },

  methods: {
    format(number) {
      return AppControl.format(number);
    },

    onNewCompany() {
      this.form = {
        company: this._currentSearch,
        country: 'FR',
        isNewCompany: true,
        selectedCompany: false
      };
      this.search = null;
      this.companies = null;
      this.establishments = null;
      gsap.to(window, { duration: .3, scrollTo: 0 });
    },

    onClear() {
      this.form = {
        isNewCompany: false,
        selectedCompany: false
      };
      this.search = null;
      this.companies = null;
      this.establishments = null;
      gsap.to(window, { duration: .3, scrollTo: 0 });
    },

    onSubmit(pEvt) {
      pEvt.preventDefault();
      if(!this._form.isValidate) return;
      if(!this.form.isNewCompany && !this.form.selectedCompany) return;
      if(this.isLoading) return;
      this.isLoading = true;
      AppControlService.update('step1', this.form).then( () => {
        AppControlService.goToNextStep();
      }).catch(console.error).then(() => {
        this.isLoading = false;
      })
    },

    async onSelectEstablishment( establishment ) {
      this.isErrorCompany = false;
      if(this.isLoadingEstablishment) return;
      this.isLoadingEstablishment = establishment;
      try {
        this.form.siren = establishment.siren;
        const establishments = await SocieteApiService.establishments( establishment.siren );
        const postal = establishment.cpville.split(' ')[0];
        const results = establishments.filter( e => e.estactive && e.codepostal === postal);
        if(results[0]) {
          this.form.company = results[0].deno
          this.form.siret = results[0].siret;
          this.form.street = results[0].voieadr;
          this.form.zipCode = results[0].codepostal;
          this.form.city = results[0].ville;
          this.form.country = countryList.findByName(results[0].pays)?.code?.iso2;
        } else if(establishments[0]) {
          this.form.company = establishments[0].deno;
          this._currentSearch = establishments[0].deno;
          this.isErrorCompany = true;
          this.form.isNewCompany = true;
          throw Error('Company informations corrupted')
        } else {
          this.form.company = establishment.raisonsociale;
          this._currentSearch = establishment.raisonsociale;
          this.isErrorCompany = true;
          this.form.isNewCompany = true;
          throw Error('Company informations corrupted')
        }

        this.form.isNewCompany = false;
        this.form.selectedCompany = true;
      } catch(err) {
        console.error(err);
        this.isSearching = false;
        this.form.isNewCompany = true;
        this.form.company = this._currentSearch;
      }
      this.search = null;
      this.isLoadingEstablishment = false;
      gsap.to(window, { duration: .3, scrollTo: 0 });
    },

    async onSelectCompany(company) {
      if(this.isLoadingCompany) return;
      this.isLoadingCompany = company;
      try {
        this.form.siren = company.siren;
        const datas = await SocieteApiService.company( company.siren );
        this.form.company = datas.denoinsee;
        this.form.siret = datas.siretsiege;
        this.form.street = datas.voieadressageinsee;
        this.form.zipCode = datas.codepostalinsee;
        this.form.city = datas.villeinsee;
        this.form.country = countryList.findByName(datas.paysinsee)?.code?.iso2;
        this.form.isNewCompany = false;
        this.form.selectedCompany = true;
      } catch(err) {
        this.isSearching = false;
        this.form.isNewCompany = true;
        this.form.company = this._currentSearch;
      }
      this.search = null;
      this.isLoadingCompany = false;
      gsap.to(window, { duration: .3, scrollTo: 0 });
    },

    onKeyup() {
      if(this.search === this._currentSearch && this._currentSearch !== '' && this._currentSearch !== null) return;
      this.companies = null;
      this.establishments = null;
      this.isSearching = true;
      this._currentSearch = this.search;
      this.getDatas();
    },

    async getDatas() {
      if(this.isLoadingCompany || this.isLoadingEstablishment) return;
      this.companies = null;
      this.establishments = null;
      if(this._currentSearch === '') return;
      try {
        let department = this.form.department;
        if(department === '2a' || department === '2b') department = '20';
        const establishments = await SocieteApiService.findEstablishments( this._currentSearch, department.slice(0, 2) );
        this.establishments = establishments;
        this.isSearching = false;
      } catch(err) {
        console.error(err);
        if(!err.__CANCEL__) {
          this.isSearching = false;
          this.form.isNewCompany = true;
          this.form.company = this._currentSearch;
        }
      }
    },

    onPrevious() {
      AppControlService.goToPrevStep();
    },

    Translator(key, datas = null) {
      return window.Translator.trans(key, datas);
    },

    _onUpdateDatas(datas) {
      this.datas = datas;
      if(this.datas.step1) this.form = this.datas.step1;
    }
  }
};
</script>
<style scoped lang="scss">
@import "~@/soprasolar-project/assets/styles/import";
.project-wrapper {
  @include vertical-spacing-inner-top;
}
.form-retry {
  @include resetBtn;
  padding: rem(5) rem(10);
  color: $color-main;
  font-size: rem(14);
  display: inline-flex;
  align-items: center;
  gap: rem(5);
  .ic {
    font-size: rem(18);
  }
}
.project-hint {
  font-size: rem(12);
  margin-top: rem(-15);
  margin-bottom: rem(15);
}
.project-fake-input {
  @include form-input;
  position: relative;
  padding: 0;
  input[type=text] {
    width: 100%;
    border: 0;
    background-color: transparent;
    padding-left: rem(35);
    padding-right: rem(35);
  }
  .ic {
    position: absolute;
    left: rem(10);
    top: 50%;
    transform: translateY(-50%);
    font-size: rem(22);
    color: $grey;
  }
  &.is-loading {
    @include loader($color-second, .7) {
      left: auto;
      right: 0;
    }
  }
}
.project-warning {
  width: 100%;
  display: flex;
  align-items: center;
  gap: rem(15);
  border: rem(1) solid $color-second;
  background-color: rgba($color-second, .1);
  padding: rem(10) rem(20);
  border-radius: rem(12);
  margin-bottom: rem(20);
  .ic {
    font-size: rem(22);
    color: $color-second;
  }
  .project-warning-message {
    font-size: rem(14);
    color: $color-second;
  }
}
.project-propositions {
  @include resetList;
  border: rem(1) solid $grey-light;
  box-shadow: 0 rem(10) rem(15) rgba($black, .12);
  border-radius: rem(4);
  padding-top: rem(10);
  .project-propositions-category {
    font-weight: bold;
    padding: rem(10) rem(25);
    color: $color-second;
  }
  .project-propositions-item {
    margin-bottom: rem(5);
    margin-left: rem(10);
    margin-right: rem(10);
    &.project-propositions-item--empty {
      text-align: center;
      color: $grey;
      padding-top: rem(20);
      padding-bottom: rem(20);
    }
    &.project-propositions-item--add {
      border-top: rem(1) solid lighten($grey-light, 10%);
      padding-top: rem(5);
      margin-top: rem(10);
    }
  }
  .project-propositions-button {
    @include resetBtn;
    position: relative;
    width: 100%;
    display: inline-block;
    padding: rem(5) rem(10);
    text-align: left;
    line-height: 1.1;
    outline: none;
    border: rem(1) solid transparent;
    border-radius: rem(5);
    transition: border-color $transition-out-hover, background-color $transition-out-hover;
    padding-right: rem(50);
    span {
      font-size: rem(12);
    }
    &:hover, &:focus-visible {
      border-color: $color-main;
      background-color: rgba($color-main, .1);
      transition: border-color $transition-in-hover, background-color $transition-in-hover;
    }
    &.is-loading {
      @include loader($color-second, .7) {
        left: auto;
        right: 0;
      }
    }
  }
  .project-propositions-add {
    @include resetBtn;
    color: $color-main;
    display: flex;
    gap: rem(10);
    align-items: center;
    padding: rem(5) rem(10);
    .ic {
      font-size: rem(22);
    }
    &:hover, &:focus-visible {
      text-decoration: underline;
    }
  }
}
</style>